import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary } from '@kollez/design-system';
import { apiGet } from '@/lib/api';

interface AcademicRecord {
  academic?: {
    cgpa?: number;
    /** Number in demo seeds; `{current, history}` object in the real schema. */
    backlogs?: number | { current?: number; history?: unknown[] };
    class10Percentage?: number;
    class12Percentage?: number;
    semesters?: Array<{ sem: number; sgpa?: number; marksPct?: number; verified?: boolean }>;
  };
}

function backlogsText(v: number | { current?: number; history?: unknown[] } | undefined): string {
  if (v == null) return '—';
  if (typeof v === 'number') return String(v);
  return String(v.current ?? 0);
}

function formatCgpa(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toFixed(2);
}

/**
 * /academics — verified academic record view (read-only; edits happen via upload-marks).
 *
 * Deep-audit fixes (Page 16/N):
 *   - Query wrapper wrapped the fetch in a .catch(() => empty profile),
 *     silently hiding real API errors — the user just saw a page full
 *     of '—' when their token was invalid or the profile was truly
 *     missing. Removed the swallow so AsyncBoundary surfaces real
 *     failures with a retry.
 *   - CGPA rendered as raw JSON.stringify (e.g., '7.5' or '7.502'
 *     depending on how it was stored). Formatted to two decimals for
 *     consistent presentation.
 */
export function StudentAcademicsPage() {
  const query = useQuery({
    queryKey: ['student', 'me', 'academics'],
    queryFn: () => apiGet<{ profile: AcademicRecord | null }>('/students/me'),
  });
  const p = query.data?.profile?.academic ?? {};
  const semesters = p.semesters ?? [];

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">My profile</p>
          <h1 className="h-page">Academic record</h1>
          <p className="subhead">Your verified marks and standing. Semester marks are added via Upload marks; the registrar signs them off.</p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-primary" to="/upload-marks">Upload marks</Link>
        </div>
      </div>

      <AsyncBoundary kind="kpis" loading={query.isLoading} error={query.error}>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <div className="card kpi">
            <span className="kpi-label">CGPA</span>
            <span className="kpi-value">{formatCgpa(p.cgpa)}</span>
            <span className="kpi-meta">up to your last semester</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Backlogs</span>
            <span className="kpi-value">{backlogsText(p.backlogs)}</span>
            <span className="kpi-meta">active only</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Class 10</span>
            <span className="kpi-value">{p.class10Percentage != null ? `${p.class10Percentage}%` : '—'}</span>
          </div>
          <div className="card kpi">
            <span className="kpi-label">Class 12</span>
            <span className="kpi-value">{p.class12Percentage != null ? `${p.class12Percentage}%` : '—'}</span>
          </div>
        </div>

        <section className="card">
          <div className="card-head"><h2 className="h-section">Semester by semester</h2><span className="helper">SGPA / marks</span></div>
          {semesters.length === 0 ? (
            <div className="p-8 text-center text-tertiary">
              Upload your semester marksheets to build this table.
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Semester</th><th className="r">SGPA</th><th className="r">Marks %</th><th>Status</th></tr></thead>
                <tbody>
                  {semesters.map((s) => (
                    <tr key={s.sem}>
                      <td>Semester {s.sem}</td>
                      <td className="r">{s.sgpa != null ? s.sgpa.toFixed(2) : '—'}</td>
                      <td className="r">{s.marksPct != null ? `${s.marksPct}%` : '—'}</td>
                      <td><span className={`badge ${s.verified ? 'badge-success' : 'badge-warning'}`}>{s.verified ? 'Verified' : 'Awaiting'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </AsyncBoundary>
    </div>
  );
}
